import { useEffect, useState } from 'react'
import { Formik } from 'formik'
import { useLazyQuery } from '@apollo/client'
import classnames from 'classnames'

import Icon from 'components/Icon'
import Alert from 'components/Alert'
import Button from 'components/Button'
import ModalDialog from 'components/ModalDialog'
import FetchingPanel from 'components/FetchingPanel'
import { FieldsetRow, FormField } from 'components/FormControls'

import useAdjustStockOnHandModal from './useAdjustStockOnHandModal'

import GET_DATA_FOR_ADJUST_INVENTORY from './graphql/GetDataForAdjustInventory.graphql'
import {
  GetDataForAdjustInventory,
  GetDataForAdjustInventoryVariables,
  GetDataForAdjustInventory_currentSeller_draftVariety,
  GetDataForAdjustInventory_currentSeller_draftVariety_catalog,
  GetDataForAdjustInventory_currentSeller_draftVariety_product
} from './graphql/__generated__/GetDataForAdjustInventory'

import styles from './AdjustStockOnHandModal.module.css'

type AdjustStockOnHandModalArguments = Pick<
  GetDataForAdjustInventory_currentSeller_draftVariety,
  'sku' | 'stockOnHand'
> &
  Pick<
    GetDataForAdjustInventory_currentSeller_draftVariety_catalog,
    'outOfStockThreshold' | 'isAvailableOnMarketplace'
  > &
  Pick<GetDataForAdjustInventory_currentSeller_draftVariety_product, 'hidden'>

type AdjustStockOnHandModalProps = {
  varietyId: string
}

const AdjustStockOnHandModal = ({ varietyId }: AdjustStockOnHandModalProps) => {
  const [data, setData] = useState<AdjustStockOnHandModalArguments>({
    sku: null,
    stockOnHand: null,
    outOfStockThreshold: null,
    isAvailableOnMarketplace: true,
    hidden: null
  })
  const [open, setOpen] = useState<boolean>(false)
  const [activeField, setActiveField] = useState<string>('newStockOnHand')

  const [getDataForAdjustInventory, { loading }] = useLazyQuery<
    GetDataForAdjustInventory,
    GetDataForAdjustInventoryVariables
  >(GET_DATA_FOR_ADJUST_INVENTORY, {
    variables: {
      varietyId
    }
  })

  useEffect(() => {
    const fetchAdjustInventoryData = async () => {
      const result = (await getDataForAdjustInventory()).data?.currentSeller
      setData({
        sku: result?.draftVariety.sku ?? '',
        stockOnHand: result?.draftVariety.stockOnHand ?? 0,
        outOfStockThreshold: result?.draftVariety.catalog.outOfStockThreshold ?? null,
        isAvailableOnMarketplace: result?.draftVariety.catalog.isAvailableOnMarketplace ?? true,
        hidden: result?.draftVariety.product.hidden ?? null
      })
    }
    if (open) {
      fetchAdjustInventoryData()
    }
  }, [getDataForAdjustInventory, open])

  const handleFocus = (event: React.ChangeEvent<HTMLInputElement>) => {
    setActiveField(event.currentTarget.name)
  }

  const closeModal = () => {
    setOpen(false)
  }

  const { initialValues, validationSchema, onSubmit } = useAdjustStockOnHandModal({
    varietyId,
    stockOnHand: data.stockOnHand ?? 0,
    closeModal
  })

  return (
    <Formik
      key={varietyId}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {formikProps => {
        const { values, setFieldValue, isSubmitting, isValid, dirty, submitForm } = formikProps

        const handleClickStockOnHandModal = () => {
          setOpen(prevState => !prevState)
          if (dirty) {
            setFieldValue('adjustBy', 0)
            setFieldValue('newStockOnHand', data.stockOnHand)
          }
          setActiveField('newStockOnHand')
        }

        const toggleIncrementButton = () => {
          setFieldValue('adjustBy', Number(values.adjustBy) + 1)
          setFieldValue('newStockOnHand', Number(values.newStockOnHand) + 1)
          setActiveField('adjustBy')
        }

        const toggleDecrementButton = () => {
          setFieldValue('adjustBy', Number(values.adjustBy) - 1)
          setFieldValue('newStockOnHand', Number(values.newStockOnHand) - 1)
          setActiveField('adjustBy')
        }

        const handleUpdateAdjustByValue = (event: React.ChangeEvent<HTMLInputElement>) => {
          setFieldValue('newStockOnHand', Number(data.stockOnHand ?? 0) + Number(event.currentTarget.value))
        }

        const handleUpdateNewStockOnHandValue = () => {
          setFieldValue('adjustBy', 0)
        }

        return (
          <>
            <Icon
              className={styles.editStockOnHandIcon}
              kind="edit"
              size={12}
              color="var(--colorGray8)"
              onClick={handleClickStockOnHandModal}
            />

            <ModalDialog
              className={styles.modalContainer}
              isOpen={open}
              title="Stock on Hand Adjustment"
              descriptor={data.sku}
              onExit={handleClickStockOnHandModal}
              buttonsRight={
                <>
                  <Button kind="default" disabled={isSubmitting} onClick={handleClickStockOnHandModal}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    kind="primary"
                    isSubmitting={isSubmitting}
                    disabled={isSubmitting || !isValid || !dirty}
                    onClick={submitForm}>
                    Update Stock on Hand
                  </Button>
                </>
              }>
              {loading ? (
                <FetchingPanel />
              ) : (
                <div className={styles.modalContent}>
                  <FieldsetRow className={styles.fieldset}>
                    <div className={styles.buttonContainer}>
                      <Button kind="default" icon="minus" onClick={toggleDecrementButton} />
                      <Button kind="default" icon="plus" onClick={toggleIncrementButton} />
                    </div>
                    <div className={styles.fieldsContainer}>
                      <FormField
                        className={styles.adjustByField}
                        titlePosition="right"
                        type="text"
                        name="adjustBy"
                        kind="primary"
                        title="Adjust By"
                        active={activeField !== 'newStockOnHand'}
                        // Allow numbers only
                        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                          !/[0-9\-]/.test(e.key) && e.preventDefault()
                        }
                        onChange={handleUpdateAdjustByValue}
                        onFocus={handleFocus}
                        alignRight
                      />
                      <p>OR</p>

                      <FormField
                        className={styles.newStockOnHandField}
                        titlePosition="right"
                        type="text"
                        name="newStockOnHand"
                        kind="primary"
                        title="New Stock on Hand"
                        active={activeField !== 'adjustBy'}
                        // Allow numbers only
                        onKeyPress={(e: React.KeyboardEvent<HTMLInputElement>) =>
                          !/[0-9\-]/.test(e.key) && e.preventDefault()
                        }
                        onChange={handleUpdateNewStockOnHandValue}
                        onFocus={handleFocus}
                        alignRight
                        autoFocus
                      />
                    </div>
                  </FieldsetRow>

                  {values.newStockOnHand <= (data.outOfStockThreshold ?? 0) &&
                  !data.hidden &&
                  data.isAvailableOnMarketplace ? (
                    <Alert
                      kind="warning"
                      className={classnames(styles.alertContainer, {
                        [styles.hasThreshold]: data.outOfStockThreshold != null
                      })}>
                      <h4 className={styles.alertTitle}>
                        Product will be marked Out of Stock on Fieldfolio Marketplace
                      </h4>

                      {data.outOfStockThreshold != null && (
                        <>
                          <h5 className={styles.subTitle}>
                            Stock on Hand is less than the Out of Stock threshold ({data.outOfStockThreshold})
                          </h5>
                          <p>
                            Products with Stock on Hand less than or equal to the Out of Stock threshold are marked ‘Out
                            of Stock’ and cannot be ordered on Fieldfolio Marketplace.
                          </p>
                        </>
                      )}
                    </Alert>
                  ) : null}

                  {values.newStockOnHand > (data.outOfStockThreshold ?? 0) &&
                  !data.hidden &&
                  data.isAvailableOnMarketplace ? (
                    <Alert
                      kind="success"
                      className={classnames(styles.alertContainer, {
                        [styles.hasThreshold]: data.outOfStockThreshold != null
                      })}>
                      <h4 className={styles.alertTitle}>Product will be available on Fieldfolio Marketplace</h4>

                      {data.outOfStockThreshold != null && (
                        <>
                          <h5 className={styles.subTitle}>
                            Stock on Hand is equal or greater than Out of Stock threshold ({data.outOfStockThreshold})
                          </h5>
                          <p>
                            Products with Stock on Hand equal or greater than the Out of Stock threshold will no longer
                            be marked ‘Out of Stock’ and will be avalable for ordering on Fieldfolio Marketplace.
                          </p>
                        </>
                      )}
                    </Alert>
                  ) : null}
                </div>
              )}
            </ModalDialog>
          </>
        )
      }}
    </Formik>
  )
}

export default AdjustStockOnHandModal
